import { formatDate, formatPhoneDisplay } from '@/utils/common';
import { formatDateUs, parseDateSafe } from '@/utils/dateHelper';
import { formatTenure, formatTenureCompact } from '@/utils/dateDuration';
import {
  formatEmploymentType as formatEmploymentTypeShared
} from '@/utils/employmentTypeHelper';

export const EMPLOYMENT_TYPE_LABELS = {
  W2: 'W2',
  '1099': '1099',
  CIM: 'CIM',
  full_time: 'Full time',
  part_time: 'Part time',
  contractor: 'Contractor',
  temporary: 'Temporary',
  intern: 'Intern',
  other: 'Other'
};

/**
 * @param {...unknown} values
 * @returns {unknown}
 */
export function pick(...values) {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return null;
}

/**
 * @param {string|Date|null|undefined} birthday
 * @returns {number|null}
 */
export function calcAge(birthday) {
  const date = parseDateSafe(birthday);
  if (!date) return null;
  const now = new Date();
  let age = now.getFullYear() - date.getFullYear();
  if (
    now.getMonth() < date.getMonth()
    || (now.getMonth() === date.getMonth() && now.getDate() < date.getDate())
  ) {
    age -= 1;
  }
  return Math.max(0, age);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatSex(value) {
  const sex = String(value || '').toLowerCase();
  if (sex === 'male') return 'Male';
  if (sex === 'female') return 'Female';
  if (!sex || sex === 'unknown') return '—';
  return String(value);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatEmploymentType(value) {
  return formatEmploymentTypeShared(value, { empty: '—' });
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatExecutiveRole(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : '';
  const str = String(value).trim();
  if (!str) return '';
  const lower = str.toLowerCase();
  if (lower === '1' || lower === 'true' || lower === 'yes') return 'Yes';
  if (lower === '0' || lower === 'false' || lower === 'no') return '';
  return str;
}

/**
 * @param {object|null} profile
 * @param {object|null} directory
 * @returns {string}
 */
export function displayNameFromSources(profile, directory) {
  const first = String(profile?.first_name || '').trim();
  const last = String(profile?.last_name || '').trim();
  const combined = [first, last].filter(Boolean).join(' ');
  if (combined) return combined;
  return pick(profile?.user?.name, directory?.name) || 'Employee';
}

/**
 * @param {string} name
 * @returns {string}
 */
export function buildInitials(name) {
  const value = String(name || '').trim();
  if (!value) return 'NA';
  return value.split(/\s+/).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('');
}

/**
 * Build a report field cell.
 * @param {string} id
 * @param {string} label
 * @param {unknown} value
 * @param {{ copyValue?: string|null, fullWidth?: boolean }} [opts]
 */
export function field(id, label, value, opts = {}) {
  const display = value == null || value === '' ? '—' : String(value);
  const copy = opts.copyValue == null || opts.copyValue === ''
    ? ''
    : String(opts.copyValue);
  return {
    id,
    label,
    display,
    copyText: copy,
    copyable: copy !== '' && copy !== '—',
    fullWidth: Boolean(opts.fullWidth)
  };
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isEmptyReportValue(value) {
  if (value == null) return true;
  const text = String(value).trim();
  return text === '' || text === '—' || text === '-' || text.toLowerCase() === 'n/a';
}

/**
 * Field only when value is present; otherwise null.
 * @param {string} id
 * @param {string} label
 * @param {unknown} value
 * @param {{ copyValue?: string|null, fullWidth?: boolean }} [opts]
 * @returns {object|null}
 */
export function optionalField(id, label, value, opts = {}) {
  if (isEmptyReportValue(value)) return null;
  return field(id, label, value, opts);
}

/**
 * Drop null/empty fields from a list.
 * @param {Array<object|null|undefined>} fields
 * @returns {object[]}
 */
export function compactFields(fields) {
  return (fields || []).filter(
    (item) => item && !isEmptyReportValue(item.display)
  );
}

/**
 * @param {string|Date|null|undefined} birthday
 * @returns {string}
 */
export function formatBirthdayDisplay(birthday) {
  if (!birthday) return '—';
  const age = calcAge(birthday);
  const dateLabel = formatDateUs(birthday) || formatDate(birthday);
  return `${dateLabel}${age != null ? ` (${age} yrs)` : ''}`;
}

/**
 * @param {string|Date|null|undefined} hireDate
 * @param {string|Date|null|undefined} endDate
 * @returns {{ hireDisplay: string, tenure: string, tenureFull: string }}
 */
export function formatHireAndTenure(hireDate, endDate) {
  if (!hireDate) {
    return { hireDisplay: '—', tenure: '', tenureFull: '' };
  }
  const tenure = formatTenureCompact(hireDate, endDate);
  const tenureFull = formatTenure(hireDate, endDate);
  const dateLabel = formatDateUs(hireDate) || formatDate(hireDate);
  const hireDisplay = tenure
    ? `${dateLabel} (${tenure})`
    : dateLabel;
  return { hireDisplay, tenure, tenureFull };
}

/**
 * @param {unknown} phone
 * @returns {{ display: string, copy: string }}
 */
export function formatPhoneField(phone) {
  if (!phone) return { display: '—', copy: '' };
  return {
    display: formatPhoneDisplay(phone),
    copy: String(phone)
  };
}

export { formatDate, formatPhoneDisplay, formatTenure, formatTenureCompact };
